import { useState } from 'react'
import {
  CloseButton,
  Button,
  Checkbox,
  Field,
  NativeSelect,
  SimpleGrid,
  Text,
  VStack,
  Dialog,
} from '@chakra-ui/react';
import { ResponsiveModal } from '../shared'
import type { AvailableState } from '../../types/config'

/** State House=0, State Senate=1, US House=2, US Senate=3 -- matches the backend Chamber enum. */
const CHAMBER_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'State House' },
  { value: 1, label: 'State Senate' },
  { value: 2, label: 'US House' },
  { value: 3, label: 'US Senate' },
]

export interface SyncFromLegiScanRequest {
  state: string
  chambers: number[]
}

interface SyncFromLegiScanModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (req: SyncFromLegiScanRequest) => void
  /** Localities the caller may sync. From useAvailableStates() -- all states + DC today,
   *  a customer-scoped subset once instance data-access config lands. */
  states: AvailableState[]
}

export function SyncFromLegiScanModal({ isOpen, onClose, onSubmit, states }: SyncFromLegiScanModalProps) {
  const [state, setState] = useState('')
  const [chambers, setChambers] = useState<number[]>([])

  const canSubmit = state !== '' && chambers.length > 0

  const toggleChamber = (value: number, checked: boolean) => {
    setChambers((prev) =>
      checked
        ? [...prev, value].sort((a, b) => a - b)
        : prev.filter((c) => c !== value),
    )
  }

  const handleSubmit = () => {
    if (!canSubmit) return
    onSubmit({ state, chambers })
  }

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="sm">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>Sync from LegiScan</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>
            <VStack align="stretch" gap={4}>
              <Field.Root w="full">
                <Field.Label fontSize="sm">State</Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    minH="44px"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                  >
                    <option value="">Select a state…</option>
                    {states.map((s) => (
                      <option key={s.code} value={s.code}>{s.name}</option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
              <VStack align="stretch" gap={2}>
                {/* NOT wrapped in a shared Field.Root: Chakra v3 makes descendant Checkbox.Label
                    ids resolve through the nearest Field context, so a common ancestor Field.Root
                    collapses all four checkboxes' accessible names onto one shared field label id
                    (mirrors the known Field.Root-overrides-descendant-id gotcha). Plain text
                    heading here keeps each checkbox's own Checkbox.Label as its accessible name. */}
                <Text fontSize="sm" fontWeight="medium">Chambers</Text>
                <SimpleGrid columns={2} gap={3}>
                  {CHAMBER_OPTIONS.map((opt) => (
                    <Checkbox.Root
                      key={opt.value}
                      size="sm"
                      minH="44px"
                      checked={chambers.includes(opt.value)}
                      onCheckedChange={(e) => toggleChamber(opt.value, e.checked === true)}
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control><Checkbox.Indicator /></Checkbox.Control>
                      <Checkbox.Label>
                        <Text fontSize="sm">{opt.label}</Text>
                      </Checkbox.Label>
                    </Checkbox.Root>
                  ))}
                </SimpleGrid>
              </VStack>
            </VStack>
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorPalette="brand" disabled={!canSubmit} onClick={handleSubmit}>
              Sync
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  );
}
